<script setup lang="ts">

import { onMounted, ref, watch } from "vue";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import { useNIRApplicationsStore } from "@/domains/home/services/nir/registration/stores";
import { useNotificationsStore } from "@/app/notify/notifications.ts";

type PushData = {
  institutionId: string,
  generateErn: boolean
}

const form = ref<PushData>({
  institutionId:"",
  generateErn: false
})

const selectedFile = ref<File | null>(null)

const emit = defineEmits(["close"])

const loading = ref(false)
const institutionStore = useInstitutionStore()
const formStore = useFormsStore()
const store = useNIRApplicationsStore()
const notify = useNotificationsStore()

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
})

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const file = target.files[0]
    const ext = file.name.split('.').pop()?.toLowerCase()
    const validExts = ['xls', 'xlsx']
    const validMimes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]

    if (!validExts.includes(ext ?? '') || !validMimes.includes(file.type)) {
      notify.error('Invalid file type. Please upload an Excel file (.xls or .xlsx)')
      target.value = ''
      selectedFile.value = null
      return
    }

    selectedFile.value = file
  }
}

function submit(){
  let formData = new FormData()
  formData.set("institutionId", form.value.institutionId)
  formData.set("generateErn", form.value.generateErn.toString())

  if (selectedFile.value) {
    formData.append("file", selectedFile.value)
  }

  loading.value = true
  store.pushData(formData)
    .then(() => {
      loading.value = false
      resetForm()
      notify.success("Successfully uploaded")
    })
      .catch(() =>{
        loading.value = false
        notify.error("Error: uploading file")
      })
}


function resetForm(){
  form.value.institutionId = ''
  form.value.generateErn = false
  selectedFile.value = null
  emit('close')
}

watch(
  () => form.value.institutionId,
  () => {
    formStore.fetchForms("", form.value.institutionId, "", 1,100)
  })

</script>

<template>
<div class="flex flex-col">
  <div class="p-2">
    <h2 class="text-lg font-bold text-gray-900 mb-4">Add Bulk Institutions</h2>

    <form @submit.prevent="submit" class="space-y-4">
      <div class="grid grid-cols-2 gap-4">

        <!-- Institution Type -->
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-1.5">
            <i class="fa-solid fa-building text-gray-400 mr-1"></i>
            Institution *
          </label>
          <select
            v-model="form.institutionId"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Select Institution</option>
            <option v-for="(type, idx) in (institutionStore.institutions ?? []).filter(i => i.status === 'Active')" :key="idx" :value="type.id">
              {{ type.name }}
            </option>
          </select>
        </div>

        <!-- Contact Phone -->
        <div class="col-span-2 flex flex-row gap-2">
          <input type="checkbox" v-model="form.generateErn" class="h-5 w-5 " >
          <span>Generate ERN</span>
        </div>
        <div class="col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-1.5">
            <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
            Data File (excel spreadsheet)*
          </label>
          <input
            type="file"
            @change="handleFileChange"
            required
            accept=".xls,.xlsx"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="+256-XXX-XXX-XXX"
          />
        </div>

      </div>

      <!-- Form Actions -->
      <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          @click="resetForm()"
          class="btn-danger"
        >
          <i class="fas fa-times mr-1"></i>
          Cancel
        </button>
        <button
          type="submit"
          :disabled="loading"
          class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i class="fa-solid fa-check mr-1"></i>
          <span v-if="!loading">Push Data</span>
          <span v-else>Creating...</span>
        </button>
      </div>
    </form>
  </div>
</div>
</template>

<style scoped></style>
